import { useMemo, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetLmsAllNewsQuery } from '../../../../../api';
import toastService from '../../../../../utils/toastService';
import { formatErrorMessage } from '../../../../../utils/errorUtils';
import { matchModeToOperator } from '../../../../../utils/filterUtils';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import useLmsNewsSave from '../../hooks/useLmsNewsSave';

const BACKEND_COLUMN_MAP = {
    title: 'Title',
    newsType: 'NewsType',
    trainingName: 'TrainingName',
    planningLocationText: 'PlanningLocationText',
    status: 'Status',
    publishDate: 'PublishDate',
    createdName: 'CreatedName',
    createdDate: 'CreatedDate'
};

const INITIAL_FILTERS = {
    title: { value: null, matchMode: FilterMatchMode.CONTAINS },
    newsType: { value: null, matchMode: FilterMatchMode.EQUALS },
    trainingName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    planningLocationText: { value: null, matchMode: FilterMatchMode.CONTAINS },
    createdName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.IN },
    publishDate: { value: null, matchMode: FilterMatchMode.DATE_IS },
    createdDate: { value: null, matchMode: FilterMatchMode.DATE_IS }
};

const buildBackendFilters = (filters) =>
    Object.entries(filters || {}).reduce((acc, [field, meta]) => {
        const column = BACKEND_COLUMN_MAP[field];
        const value = meta?.value;
        if (!column || value === null || value === undefined || String(value).trim() === '') return acc;
        if (Array.isArray(value)) {
            value.forEach((item) => acc.push({ column, operator: matchModeToOperator[FilterMatchMode.EQUALS], value: String(item) }));
            return acc;
        }
        acc.push({ column, operator: matchModeToOperator[meta.matchMode] ?? matchModeToOperator[FilterMatchMode.CONTAINS], value: value instanceof Date ? value.toISOString() : String(value).trim() });
        return acc;
    }, []);

const useLmsNewsManagementData = () => {
    const { th } = useLanguage();
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1 });
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [sortField, setSortField] = useState('publishDate');
    const [sortOrder, setSortOrder] = useState(-1);
    const { removeNews } = useLmsNewsSave();

    const queryParams = useMemo(() => {
        const sortColumn = BACKEND_COLUMN_MAP[sortField];
        return {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            filters: buildBackendFilters(filters),
            sorts: sortColumn && sortOrder ? [{ column: sortColumn, direction: sortOrder === 1 ? 1 : 2 }] : [{ column: 'PublishDate', direction: 2 }]
        };
    }, [lazyParams, filters, sortField, sortOrder]);

    const { data, isLoading, isFetching, refetch } = useGetLmsAllNewsQuery(queryParams, { refetchOnMountOrArgChange: true });

    const onPage = (event) => {
        setLazyParams({ first: event.first, rows: event.rows, page: event.page + 1 });
    };

    const onSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
    };

    const onFilter = (event) => {
        setFilters(event.filters);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const clearFilters = () => {
        setFilters(INITIAL_FILTERS);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const deleteNews = async (id) => {
        try {
            await removeNews(id);
            toastService.success(th('lmsNews.deleteSuccess'), th('common.success'));
            refetch();
        } catch (err) {
            toastService.error(formatErrorMessage(err) || th('lmsNews.deleteError'), th('common.error'));
        }
    };

    return {
        items: data?.items || [],
        totalCount: data?.totalCount || 0,
        loading: isLoading || isFetching,
        lazyParams,
        onPage,
        sortField,
        sortOrder,
        onSort,
        filters,
        onFilter,
        clearFilters,
        deleteNews,
        refetch
    };
};

export default useLmsNewsManagementData;
